// src/Payroll_Management/PayRoll_Report/claAllowanceReport_Print_Type4.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Printer, X } from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";

const BASE_URL = "http://nissi-india.com"; // change if needed

async function axiosGetJsonDebug(url, config = {}) {
  try {
    const res = await axios.get(url, {
      ...config,
      transformResponse: [(data) => data],
      validateStatus: () => true,
      headers: {
        Accept: "application/json",
        ...(config.headers || {}),
      },
    });

    const contentType = res.headers?.["content-type"] || "";
    const raw = typeof res.data === "string" ? res.data : JSON.stringify(res.data);

    console.log("====== PRINT DEBUG ======");
    console.log("URL:", url);
    console.log("STATUS:", res.status);
    console.log("CONTENT-TYPE:", contentType);
    console.log("RAW(first 200):", raw.slice(0, 200));
    console.log("=========================");

    if (
      raw.trim().startsWith("<!doctype") ||
      raw.trim().startsWith("<html") ||
      contentType.includes("text/html")
    ) {
      throw new Error(
        `API returned HTML instead of JSON.\nURL: ${url}\nStatus: ${res.status}\nFirst: ${raw.slice(0, 120)}`
      );
    }

    let json;
    try {
      json = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
    } catch (e) {
      throw new Error(
        `Failed to parse JSON.\nURL: ${url}\nStatus: ${res.status}\nFirst: ${raw.slice(0, 120)}`
      );
    }

    if (res.status >= 400) {
      throw new Error(json?.message || `HTTP ${res.status}`);
    }

    return json;
  } catch (err) {
    console.error("PRINT DEBUG ERROR:", err);
    throw err;
  }
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDDMMYYYY(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function money2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

const CLAPrintType4 = () => {
  const nav = useNavigate();
  const q = useQuery();
  const { isDark } = useContext(ThemeContext);

  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const branchId = q.get("branchId") || localStorage.getItem("branchId") || "";
  const branchName = q.get("branchName") || ""; // optional

  // optional filters (same as report page)
  const deptName = q.get("deptName") || "";
  const deptCode = q.get("deptCode") || "";

  const fromDate = q.get("fromDate") || q.get("from") || q.get("fdat") || "";
  const toDate = q.get("toDate") || q.get("to") || q.get("tdat") || "";

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  const printContainerId = "cla-print-area";

  const fetchPrint = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || String(branchId) === "Select") {
      toast.warn("Please Select Branch.");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warn("Select Date (both FromDate and ToDate required)");
      return;
    }

    try {
      setLoading(true);
      const url = `${BASE_URL}/api/cla-allowance/print/type4`;

      const data = await axiosGetJsonDebug(url, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId),
          branchName: branchName || undefined,
          deptName: deptName || undefined,
          deptCode: deptCode || undefined,
          fromDate,
          toDate,
        },
        timeout: 60000,
      });

      if (data?.success) {
        setCompany(data.company || null);
        setMeta(data.meta || null);
        setRows(data.rows || []);
        setGrandTotal(Number(data.grandTotal || 0));
      } else {
        toast.error(data?.message || "Failed to load print data.");
      }
    } catch (e) {
      console.error("Fetch Error:", e);
      toast.error(e?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => nav(-1);

  const handlePrint = () => {
    const el = document.getElementById(printContainerId);
    if (!el) return;

    const printWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!printWindow) {
      toast.warn("Popup blocked. Please allow popups.");
      return;
    }

    const html = `
      <html>
        <head>
          <title>CLA Allowance Report</title>
          <style>
            @page { size: A4 portrait; margin: 10mm; }
            body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
            * { box-sizing: border-box; }

            .sheet { width: 100%; padding: 6mm; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th, td { border: 1px solid #000; padding: 6px 6px; vertical-align: top; }
            thead th { font-weight: 700; text-align: center; }

            .num { text-align: right; white-space: nowrap; }
            .center { text-align: center; }
            .nowrap { white-space: nowrap; }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>
          <div class="sheet">
            ${el.outerHTML}
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const companyLines = useMemo(() => {
    const c = company || {};
    return {
      name: c.companyName || "Nissi Infotech",
      sub1: c.sub || "Nissi",
      sub2: c.category || "CIVIL CONSTRUCTION",
      line1: c.line1 || "3/42-12, Nagappa colony , kavundampalayam",
      city: c.city || "CBE",
      pincode: c.pincode || "641030",
      phone: c.phone || "9003744644",
      state: c.state || "Tamil Nadu",
      country: c.country || "India",
      email: c.email || "infraconsoft-implementation@nissiinfotech.in",
    };
  }, [company]);

  const usedFrom = meta?.fromDate || fromDate;
  const usedTo = meta?.toDate || toDate;

  const reportTitle = `CLA ALLOWANCE REPORT AS ON ${formatDDMMYYYY(usedFrom)} to ${formatDDMMYYYY(usedTo)}`;
  const branchLabel = branchName || meta?.branchName || "HEAD-OFFICE";

  const pageBg = isDark ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900";

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen ${pageBg}`}>
        {/* Top bar (screen only) */}
        <div className="w-full px-6 py-4 flex items-center justify-between border-b border-black/10 bg-white">
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 hover:bg-gray-50"
          >
            <X className="w-4 h-4" />
            Close
          </button>

          <button
            onClick={handlePrint}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="w-full flex justify-center py-16">
              <div className="h-6 w-6 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <div
                id={printContainerId}
                className="bg-white text-black shadow-lg"
                style={{
                  width: "210mm",
                  minHeight: "297mm",
                  padding: "6mm",
                }}
              >
                <div style={{ border: "2px solid #000", padding: "6mm" }}>
                  {/* Header */}
                  <div
                    style={{
                      width: "100%",
                      display: "grid",
                      gridTemplateColumns: "28% 72%",
                      gap: "10px",
                      alignItems: "start",
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid #000",
                        height: "75px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    >
                      LOGO
                    </div>

                    <div style={{ textAlign: "center", lineHeight: 1.1, paddingTop: "2px" }}>
                      <div style={{ fontSize: "18px", fontWeight: 700 }}>{companyLines.name}</div>
                      <div style={{ fontSize: "12px", fontWeight: 700, marginTop: "2px" }}>{companyLines.sub1}</div>
                      <div style={{ fontSize: "12px", fontWeight: 700 }}>{companyLines.sub2}</div>

                      <div style={{ fontSize: "11px", marginTop: "4px" }}>{companyLines.line1}</div>
                      <div style={{ fontSize: "11px" }}>
                        {companyLines.city} &nbsp; {companyLines.pincode}
                      </div>
                      <div style={{ fontSize: "11px" }}>Phone No : {companyLines.phone}</div>
                      <div style={{ fontSize: "11px" }}>
                        {companyLines.state} . {companyLines.country} .
                      </div>
                      <div style={{ fontSize: "11px", marginTop: "2px" }}>{companyLines.email}</div>
                    </div>
                  </div>

                  {/* Title */}
                  <div
                    style={{
                      marginTop: "10px",
                      borderTop: "1px solid #000",
                      borderBottom: "1px solid #000",
                      textAlign: "center",
                      padding: "6px 8px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    {reportTitle}
                  </div>

                  {/* Branch */}
                  <div
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "center",
                      padding: "6px 8px",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    Branch : &nbsp; {branchLabel}
                  </div>

                  {/* Table */}
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", fontSize: "11px" }}>
                    <thead>
                      <tr>
                        <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>S.No</th>
                        <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Department Code</th>
                        <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Department Name</th>
                        <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Total Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!rows || rows.length === 0 ? (
                        <tr>
                          <td colSpan={4} style={{ border: "1px solid #000", padding: "14px", textAlign: "center" }}>
                            No Records Found
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={`row-${idx}`}>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{idx + 1}</td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>
                              {r.DeptCode || ""}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px" }}>{r.DeptName || ""}</td>
                            <td
                              style={{
                                border: "1px solid #000",
                                padding: "6px",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                                fontWeight: 700,
                              }}
                            >
                              {money2(r.TotalAmount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* Grand total box (right aligned) */}
                  <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
                    <div
                      style={{
                        border: "1px solid #000",
                        width: "42%",
                        display: "grid",
                        gridTemplateColumns: "55% 45%",
                      }}
                    >
                      <div style={{ padding: "10px 12px", textAlign: "center", fontSize: "12px" }}>Grand Total</div>
                      <div
                        style={{
                          padding: "10px 12px",
                          borderLeft: "1px solid #000",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {money2(grandTotal)}
                      </div>
                    </div>
                  </div>

                  {/* Signatures row */}
                  <div
                    style={{
                      width: "100%",
                      border: "1px solid #000",
                      borderTop: "0",
                      display: "grid",
                      gridTemplateColumns: "33.33% 33.33% 33.33%",
                      marginTop: "12px",
                    }}
                  >
                    <div style={{ padding: "18px 10px", fontWeight: 700 }}>Prepared By</div>
                    <div style={{ padding: "18px 10px", borderLeft: "1px solid #000", fontWeight: 700 }}>
                      Checked By
                    </div>
                    <div style={{ padding: "18px 10px", borderLeft: "1px solid #000", fontWeight: 700 }}>
                      Authorized Signature
                    </div>
                  </div>
                </div>
              </div>
              {/* End print area */}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CLAPrintType4;
